/**
 * DecompositionListView — Step 7
 * ──────────────────────────────
 *
 * Spreadsheet-style list of all known assets with their domain assignment,
 * key metrics, and inline domain reassignment.
 *
 * Rendered inside the Workbench center pane when the Step 4 view-switcher
 * is set to 'list'.
 *
 * Data sources:
 *   - assetGraph (from parent state, CA guided_analyze_asset_graph.json)
 *     → provides asset names, types, and relationship counts.
 *   - Domain contract / proposals (same Step 3 load precedence as Steps 5 & 6)
 *     → provides domain assignment, paragraph membership, confidence.
 *
 * Columns:
 *   Asset | Type | Domain | Confidence | Paragraphs | Connections | Status
 *
 * Deferred (not in this step):
 *   - Lines of code column — not present in assetGraph or domain contract.
 *   - Cross-view row selection sync — requires parent state lift (Step 8).
 *   - Column visibility toggle — not in Step 7 acceptance criteria.
 *
 * Inline reassignment:
 *   - Available for COBOL_PROGRAM type assets only.
 *   - Moves all paragraph members whose source_program matches the asset name
 *     from the old domain to the selected domain.
 *   - Saves draft immediately via POST /defineDomains/save-draft.
 *   - If the program has no tracked paragraphs, reassignment is a no-op
 *     against the contract but the row domain display still updates locally.
 *
 * Row selection:
 *   - Click a row to highlight it. Local highlight only — cross-view sync
 *     is deferred to Step 8.
 *
 * Props:
 *   assetGraph      {object|null}  from parent state
 *   scoutAccountId  {string}
 *   applicationName {string}
 *   userId          {string}
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Search, ArrowUpDown, ArrowUp, ArrowDown, Loader2, List, CheckCircle2, Circle } from 'lucide-react'

import {
  findDefineDomainsJobId,
  getDomainContract,
  getDomainProposals,
  saveDomainDraft,
} from '../../services/domainsService.js'

import './DecompositionListView.css'


// ── Constants ─────────────────────────────────────────────────────────────────

/** 12-colour domain palette — same as DomainsGraphView for visual consistency. */
const DOMAIN_PALETTE = [
  '#818cf8', '#34d399', '#f59e0b', '#60a5fa',
  '#f472b6', '#a78bfa', '#2dd4bf', '#fb923c',
  '#4ade80', '#f87171', '#38bdf8', '#c084fc',
]

const ASSET_TYPE_LABELS = {
  COBOL_PROGRAM: 'Program',  COPYBOOK:     'Copybook',
  DB2_TABLE:     'DB2',      DB2_DCLGEN:   'DB2',
  VSAM_DATASET:  'VSAM',     DATA_FILE:    'File',
  LOGICAL_FILE:  'File',     JCL_JOB:      'JCL',
  BMS_MAP:       'BMS',
}

/** Only programs have paragraph-level members and can be reassigned. */
const REASSIGNABLE_TYPES = new Set(['COBOL_PROGRAM'])

const SCHEMA_VERSION = 1

const columnHelper = createColumnHelper()


// ── Pure helpers ──────────────────────────────────────────────────────────────

function buildDomainColorMap(domains) {
  const map = {}
  domains.forEach((d, i) => { map[d.domain_id] = DOMAIN_PALETTE[i % DOMAIN_PALETTE.length] })
  return map
}

/**
 * programName → { domainId, domainName, confidence, source }
 * Majority-vote: program goes to the domain containing the most of its paragraphs.
 */
function buildProgramToDomainMap(domains, domainColorMap) {
  const counts = {} // programName → { domainId: count }
  domains.forEach(d => {
    ;(d.members || []).forEach(m => {
      const prog = (m.source_program || '').trim()
      if (!prog) return
      if (!counts[prog]) counts[prog] = {}
      counts[prog][d.domain_id] = (counts[prog][d.domain_id] || 0) + 1
    })
  })
  const domainById = {}
  domains.forEach(d => { domainById[d.domain_id] = d })

  const result = {}
  Object.entries(counts).forEach(([prog, domCounts]) => {
    const [bestDomId] = Object.entries(domCounts).sort((a, b) => b[1] - a[1])[0]
    const dom = domainById[bestDomId]
    result[prog] = {
      domainId:   bestDomId,
      domainName: dom?.name ?? null,
      domainColor: domainColorMap[bestDomId] ?? null,
      confidence: dom?.confidence ?? null,
      source:     dom?.source ?? null,
    }
  })
  return result
}

/** assetId → number of relationships (in + out). */
function computeConnectionCounts(relationships) {
  const counts = {}
  ;(relationships || []).forEach(r => {
    counts[r.from] = (counts[r.from] || 0) + 1
    counts[r.to]   = (counts[r.to]   || 0) + 1
  })
  return counts
}

/** programName → total paragraph members across all domains. */
function computeParagraphCounts(domains) {
  const counts = {}
  domains.forEach(d => {
    ;(d.members || []).forEach(m => {
      const prog = (m.source_program || '').trim()
      if (prog) counts[prog] = (counts[prog] || 0) + 1
    })
  })
  return counts
}

/** Build flat row data from assetGraph + derived domain maps. */
function buildRows(assetGraph, programToDomainMap, connectionCounts, paragraphCounts) {
  if (!assetGraph?.assets?.length) return []
  return assetGraph.assets.map(a => {
    const domInfo = programToDomainMap[a.name] ?? null
    return {
      id:             a.id,
      name:           a.name,
      type:           a.type,
      typeLabel:      ASSET_TYPE_LABELS[a.type] || (a.type || 'Unknown'),
      domainId:       domInfo?.domainId    ?? null,
      domainName:     domInfo?.domainName  ?? null,
      domainColor:    domInfo?.domainColor ?? null,
      confidence:     domInfo?.confidence  ?? null,
      domainSource:   domInfo?.source      ?? null,
      paragraphCount: paragraphCounts[a.name]   ?? 0,
      connectionCount: connectionCounts[a.id]   ?? 0,
      isAssigned:     domInfo !== null,
    }
  })
}

/**
 * Builds a Step-1-compatible domain contract payload for save-draft.
 * Mirrors the shape produced by DomainsBoardView._buildDraftContract.
 */
function buildContractPayload({ domainContractId, scoutAccountId, applicationName, sourceRefs, domains }) {
  const now = new Date().toISOString().replace(/\.\d{3}Z$/, 'Z')
  return {
    schema_version:              SCHEMA_VERSION,
    domain_contract_id:          domainContractId || `jsb_dom_list_${Date.now().toString(36)}`,
    account_id:                  scoutAccountId,
    application_name:            applicationName,
    status:                      'draft',
    created_at:                  now,
    created_by:                  'user',
    finalized_at:                null,
    finalized_by:                null,
    source_analysis_model_refs:  (sourceRefs?.length) ? sourceRefs : ['s3://placeholder/source_analysis'],
    parent_domain_contract_id:   null,
    revision_number:             1,
    supersedes_domain_contract_id: null,
    domains: domains.map(d => ({
      domain_id:   d.domain_id,
      name:        d.name,
      description: d.description ?? null,
      source:      d.source || 'user_modified',
      confidence:  typeof d.confidence === 'number' ? d.confidence : null,
      user_pinned: !!d.user_pinned,
      members:     (d.members || []).map(m => ({
        member_id:      m.member_id,
        name:           m.name,
        type:           m.type || 'paragraph',
        source_program: m.source_program,
        model_ref:      m.model_ref || `model://procedure/${m.source_program || 'UNKNOWN'}/${m.name}`,
      })),
      evidence: d.evidence ?? null,
    })),
    notes: null,
  }
}


// ── Sort icon helper ──────────────────────────────────────────────────────────

function SortIcon({ sorted }) {
  if (sorted === 'asc')  return <ArrowUp  size={12} className="dlv-sort-icon active" />
  if (sorted === 'desc') return <ArrowDown size={12} className="dlv-sort-icon active" />
  return <ArrowUpDown size={12} className="dlv-sort-icon" />
}


// ── Main component ────────────────────────────────────────────────────────────

export default function DecompositionListView({ assetGraph, scoutAccountId, applicationName, userId, onSelectionChange }) {

  // ── Domain data state ─────────────────────────────────────────────────────
  const [loadState,       setLoadState]       = useState('loading') // 'loading'|'ok'|'error'
  const [dataSource,      setDataSource]      = useState(null)      // 'contract'|'proposals'|'none'
  const [domains,         setDomains]         = useState([])
  const [domainContractId, setDomainContractId] = useState(null)
  const [sourceRefs,      setSourceRefs]      = useState([])

  // ── Table state ───────────────────────────────────────────────────────────
  const [sorting,       setSorting]       = useState([])
  const [globalFilter,  setGlobalFilter]  = useState('')
  const [selectedRowId, setSelectedRowId] = useState(null) // local highlight only

  // ── Save state ────────────────────────────────────────────────────────────
  const [saveStatus, setSaveStatus] = useState('idle') // 'idle'|'saving'|'saved'|'error'


  // ── Load domain data ──────────────────────────────────────────────────────
  // Named async function pattern — all setState inside fn body, not in sync
  // effect body, to satisfy react-hooks/set-state-in-effect rule.

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!scoutAccountId || !applicationName) {
        if (!cancelled) { setLoadState('ok'); setDataSource('none') }
        return
      }
      setLoadState('loading')
      try {
        const jobId = await findDefineDomainsJobId(scoutAccountId, applicationName)
        if (cancelled) return
        if (!jobId) {
          setDomains([])
          setDataSource('none')
          setLoadState('ok')
          return
        }

        // 1. Try saved contract first
        try {
          const contract = await getDomainContract(jobId)
          if (cancelled) return
          if (contract?.domains?.length) {
            setDomains(contract.domains.map(d => ({ ...d, members: d.members || [] })))
            setDomainContractId(contract.domain_contract_id || null)
            setSourceRefs(contract.source_analysis_model_refs || [])
            setDataSource('contract')
            setLoadState('ok')
            return
          }
        } catch (e) { void e }

        // 2. Fall back to AI proposals
        try {
          const proposals = await getDomainProposals(jobId)
          if (cancelled) return
          if (proposals?.domains?.length) {
            setDomains(proposals.domains.map(d => ({ ...d, members: d.members || [] })))
            setDomainContractId(null)
            setSourceRefs([])
            setDataSource('proposals')
            setLoadState('ok')
            return
          }
        } catch (e) { void e }

        // 3. No data
        if (!cancelled) {
          setDomains([])
          setDataSource('none')
          setLoadState('ok')
        }
      } catch (e) {
        void e
        if (!cancelled) setLoadState('error')
      }
    }
    load()
    return () => { cancelled = true }
  }, [scoutAccountId, applicationName])


  // ── Derived maps ──────────────────────────────────────────────────────────

  const domainColorMap    = useMemo(() => buildDomainColorMap(domains), [domains])

  const programToDomainMap = useMemo(
    () => buildProgramToDomainMap(domains, domainColorMap),
    [domains, domainColorMap]
  )

  const connectionCounts  = useMemo(
    () => computeConnectionCounts(assetGraph?.relationships),
    [assetGraph]
  )

  const paragraphCounts   = useMemo(() => computeParagraphCounts(domains), [domains])

  const rows = useMemo(
    () => buildRows(assetGraph, programToDomainMap, connectionCounts, paragraphCounts),
    [assetGraph, programToDomainMap, connectionCounts, paragraphCounts]
  )


  // ── Inline domain reassignment ────────────────────────────────────────────

  const handleReassign = useCallback(async (programName, newDomainId) => {
    // Compute next domain state: pull all members for this program, place in target
    const next = domains.map(d => ({ ...d, members: [...(d.members || [])] }))
    const collected = []
    for (const d of next) {
      d.members = d.members.filter(m => {
        if ((m.source_program || '').trim() === programName) {
          collected.push(m)
          return false
        }
        return true
      })
    }
    if (newDomainId) {
      const target = next.find(d => d.domain_id === newDomainId)
      if (target) {
        target.members = [...target.members, ...collected]
        target.source  = 'user_modified'
      }
    }

    setDomains(next)

    // Immediate save-draft
    setSaveStatus('saving')
    try {
      const contract = buildContractPayload({
        domainContractId, scoutAccountId, applicationName, sourceRefs, domains: next,
      })
      await saveDomainDraft({ scoutAccountId, applicationName, userId, contract })
      setSaveStatus('saved')
      setTimeout(() => setSaveStatus('idle'), 2500)
    } catch (e) {
      void e
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 3500)
    }
  }, [domains, domainContractId, scoutAccountId, applicationName, sourceRefs, userId])


  // ── Column definitions ────────────────────────────────────────────────────

  const columns = useMemo(() => [

    // Asset name — globally filtered
    columnHelper.accessor('name', {
      header: 'Asset',
      enableSorting: true,
      cell: info => (
        <span className="dlv-cell-name" title={info.getValue()}>
          {info.getValue()}
        </span>
      ),
    }),

    // Asset type
    columnHelper.accessor('typeLabel', {
      header: 'Type',
      enableSorting: true,
      cell: info => {
        const row = info.row.original
        const color = {
          COBOL_PROGRAM: '#60a5fa', COPYBOOK: '#a78bfa',
          DB2_TABLE: '#f59e0b', DB2_DCLGEN: '#f59e0b',
          VSAM_DATASET: '#34d399', DATA_FILE: '#34d399', LOGICAL_FILE: '#34d399',
          JCL_JOB: '#8b92a5', BMS_MAP: '#8b92a5',
        }[row.type] || '#8b92a5'
        return (
          <span className="dlv-type-badge" style={{ color, borderColor: `${color}44` }}>
            {info.getValue()}
          </span>
        )
      },
    }),

    // Domain — badge for non-program types, dropdown for programs
    columnHelper.accessor('domainId', {
      header: 'Domain',
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.domainName || ''
        const b = rowB.original.domainName || ''
        return a.localeCompare(b)
      },
      cell: info => {
        const row = info.row.original
        if (!REASSIGNABLE_TYPES.has(row.type)) {
          // Non-program: just show domain badge (read-only)
          return row.domainId ? (
            <span className="dlv-domain-badge" style={{
              background: `${row.domainColor}20`,
              borderColor: `${row.domainColor}55`,
              color: row.domainColor,
            }}>
              <span className="dlv-domain-swatch" style={{ background: row.domainColor }} />
              {row.domainName}
            </span>
          ) : (
            <span className="dlv-unassigned">—</span>
          )
        }
        // COBOL_PROGRAM: reassignment dropdown
        return (
          <select
            className="dlv-domain-select"
            value={row.domainId || ''}
            style={row.domainColor ? { borderColor: `${row.domainColor}66` } : undefined}
            onChange={e => handleReassign(row.name, e.target.value || null)}
            onClick={e => e.stopPropagation()}
          >
            <option value="">Unassigned</option>
            {domains.map(d => (
              <option key={d.domain_id} value={d.domain_id}>{d.name}</option>
            ))}
          </select>
        )
      },
    }),

    // Confidence — from assigned domain; shown as percentage
    columnHelper.accessor('confidence', {
      header: 'Confidence',
      enableSorting: true,
      sortUndefined: -1,  // nulls sort last
      cell: info => {
        const val = info.getValue()
        if (val == null) return <span className="dlv-muted">—</span>
        const pct   = Math.round(val * 100)
        const color = pct >= 80 ? '#34d399' : pct >= 60 ? '#f59e0b' : '#f87171'
        return (
          <span className="dlv-conf-cell">
            <span className="dlv-conf-bar-wrap">
              <span className="dlv-conf-bar" style={{ width: `${pct}%`, background: color }} />
            </span>
            <span className="dlv-conf-pct" style={{ color }}>{pct}%</span>
          </span>
        )
      },
    }),

    // Paragraph count — members whose source_program is this asset
    columnHelper.accessor('paragraphCount', {
      header: 'Paragraphs',
      enableSorting: true,
      cell: info => (
        <span className={`dlv-num-cell ${info.getValue() === 0 ? 'dlv-muted' : ''}`}>
          {info.getValue()}
        </span>
      ),
    }),

    // Connection count — inbound + outbound relationship edges
    columnHelper.accessor('connectionCount', {
      header: 'Connections',
      enableSorting: true,
      cell: info => (
        <span className={`dlv-num-cell ${info.getValue() === 0 ? 'dlv-muted' : ''}`}>
          {info.getValue()}
        </span>
      ),
    }),

    // Assigned / Unassigned status
    columnHelper.accessor('isAssigned', {
      header: 'Status',
      enableSorting: true,
      cell: info => info.getValue()
        ? <span className="dlv-status-chip dlv-status-assigned"><CheckCircle2 size={11} />Assigned</span>
        : <span className="dlv-status-chip dlv-status-unassigned"><Circle size={11} />Unassigned</span>,
    }),

  // handleReassign and domains are in the closure; rebuild when they change
  ], [domains, handleReassign])


  // ── TanStack Table instance ───────────────────────────────────────────────

  const table = useReactTable({
    data: rows,
    columns,
    state:                { sorting, globalFilter },
    onSortingChange:      setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel:      getCoreRowModel(),
    getSortedRowModel:    getSortedRowModel(),
    getFilteredRowModel:  getFilteredRowModel(),
    // Filter only on the asset name — avoids false matches on numeric columns
    globalFilterFn: (row, _colId, filterValue) => {
      const name = String(row.original.name || '').toLowerCase()
      return name.includes(String(filterValue || '').toLowerCase())
    },
  })


  // ── Render ────────────────────────────────────────────────────────────────

  if (loadState === 'loading') {
    return (
      <div className="dlv-root dlv-state-center">
        <Loader2 size={16} className="dlv-spin" />
        <span>Loading domain data…</span>
      </div>
    )
  }

  const totalRows    = table.getPreFilteredRowModel().rows.length
  const filteredRows = table.getRowModel().rows.length

  return (
    <div className="dlv-root" id="dc-decomp-panel-list" role="tabpanel">

      {/* ── Toolbar ───────────────────────────────────────────────────────── */}
      <div className="dlv-toolbar">
        <div className="dlv-toolbar-left">
          <span className="dlv-title">Asset List</span>

          {dataSource === 'contract'  && <span className="dlv-tag dlv-tag-contract">Contract</span>}
          {dataSource === 'proposals' && <span className="dlv-tag dlv-tag-proposals">AI Proposals</span>}
          {dataSource === 'none'      && <span className="dlv-tag dlv-tag-none">No domain data</span>}

          <span className="dlv-row-count">
            {globalFilter
              ? `${filteredRows} of ${totalRows} assets`
              : `${totalRows} assets`}
          </span>
        </div>

        <div className="dlv-toolbar-right">
          {/* Save indicator */}
          {saveStatus === 'saving' && (
            <span className="dlv-save dlv-save-saving">
              <Loader2 size={12} className="dlv-spin" /> Saving…
            </span>
          )}
          {saveStatus === 'saved' && (
            <span className="dlv-save dlv-save-ok">
              <CheckCircle2 size={12} /> Saved
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="dlv-save dlv-save-error">Save failed</span>
          )}

          {/* Search */}
          <div className="dlv-search-wrap">
            <Search size={13} className="dlv-search-ico" />
            <input
              className="dlv-search"
              type="text"
              placeholder="Filter assets…"
              value={globalFilter}
              onChange={e => setGlobalFilter(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      {rows.length === 0 ? (
        <div className="dlv-state-center dlv-empty">
          <List size={28} />
          <span>No asset graph available.</span>
          <span className="dlv-state-sub">
            Run Code Analysis to generate asset data, then return here.
          </span>
        </div>
      ) : (
        <div className="dlv-table-wrap">
          <table className="dlv-table">
            <thead className="dlv-thead">
              {table.getHeaderGroups().map(hg => (
                <tr key={hg.id}>
                  {hg.headers.map(header => (
                    <th
                      key={header.id}
                      className={`dlv-th ${header.column.getCanSort() ? 'dlv-th-sortable' : ''}`}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <span className="dlv-th-inner">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <SortIcon sorted={header.column.getIsSorted()} />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody className="dlv-tbody">
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="dlv-no-results">
                    No assets match "{globalFilter}"
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map(row => (
                  <tr
                    key={row.id}
                    className={`dlv-tr ${selectedRowId === row.original.id ? 'dlv-tr-selected' : ''}`}
                    onClick={() => {
                      const isDeselecting = selectedRowId === row.original.id
                      setSelectedRowId(isDeselecting ? null : row.original.id)
                      onSelectionChange?.(isDeselecting ? null : { type: 'asset', data: row.original })
                    }}
                  >
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="dlv-td">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
